import { useId } from "react";
import { motion, useReducedMotion } from "motion/react";
import cx from "classnames";
import type { SegmentedControlProps } from "./types";
import styles from "./segmentedControl.module.scss";

const SegmentedControl = <Value extends string>({
  id,
  "aria-label": ariaLabel,
  options,
  value,
  onChange,
  disabled = false,
  className,
}: SegmentedControlProps<Value>) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const reduceMotion = useReducedMotion();

  return (
    <div
      id={controlId}
      className={cx(styles.segmentedControl, className)}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map(option => (
        <button
          key={option.value}
          className={styles.option}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled || option.disabled}
          onClick={() => {
            if (!disabled && !option.disabled && option.value !== value) onChange(option.value);
          }}
        >
          {option.value === value && (
            <motion.span
              className={styles.indicator}
              aria-hidden="true"
              layoutId={reduceMotion ? undefined : `${controlId}-indicator`}
              initial={false}
              transition={{ type: "spring", stiffness: 350, damping: 28, mass: 1 }}
            />
          )}
          <span className={styles.label}>{option.label}</span>
        </button>
      ))}
    </div>
  );
};

export default SegmentedControl;
